import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useGatewayCreateMutation, useGatewayDeleteMutation } from 'hooks/mutations/origin'
import { originGatewaysOverviewRoute } from 'pages/NWPlatform/OriginGateways'
import { gatewayRoute } from 'pages/NWPlatform/OriginGateways/Gateway/Routing'
import { gatewayQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { gatewayId } = gatewayRoute.useParams()
  const navigate = useNavigate({ from: gatewayRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const gatewayQuery = useQuery(gatewayQueryKeys.detail({ gatewayId }))
  const { data: gateway } = gatewayQuery

  const { mutate: createGateway, ...gatewayCreateMutation } = useGatewayCreateMutation({ originId: gatewayId })
  const { mutate: deleteGateway } = useGatewayDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!gateway) return

    setAnchorEl(undefined)

    deleteGateway(
      { gatewayId: gateway.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:OriginGateway.nameDelete', { name: gateway.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: originGatewaysOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteGateway, enqueueSnackbar, gateway, navigate, t])

  const handleDuplicateGateway = useCallback(() => {
    if (!gateway) return

    setAnchorEl(undefined)

    createGateway(
      { originGatewayInsert: { ...gateway, name: `${gateway.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [gateway, createGateway, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={gateway?.name}
        entity={t('entity:origin.gateway', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateGateway}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {gatewayCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={gatewayId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:OriginGateway.deleteGateway', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
